import type { SVGProps } from 'react'

/**
 * Mini brand icons — hand-drawn 24x24 stroke icons (1.8 width, round caps).
 * Strong geometric shapes, NOT emoji. Each has a signature micro-detail.
 * Hover animation is handled by `.mini-icon` CSS (lift + glow + draw sweep).
 */

type P = SVGProps<SVGSVGElement>

function Base({ children, ...props }: P) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      {children}
    </svg>
  )
}

/** AI — hexagon core + orbiting spark node */
export function MiniAi(props: P) {
  return (
    <Base {...props}>
      <path d="M12 2.8 19 6.8v8.4l-7 4-7-4V6.8l7-4Z" />
      <circle cx="12" cy="12" r="2.2" fill="currentColor" stroke="none" />
      <circle cx="18.2" cy="5.4" r="1.3" fill="currentColor" stroke="none" />
      <path d="M15.8 6.6 13.6 8.2" strokeWidth={1.4} />
    </Base>
  )
}

/** Design — pen nib in tilted square */
export function MiniDesign(props: P) {
  return (
    <Base {...props}>
      <rect x="3.5" y="3.5" width="17" height="17" rx="4.5" />
      <path d="M12 7.2 15.8 11l-4.4 4.4-3.9.9.9-3.9L12 7.2Z" />
      <circle cx="12" cy="14.6" r="0.9" fill="currentColor" stroke="none" />
    </Base>
  )
}

/** Code — angle brackets + center slash node */
export function MiniCode(props: P) {
  return (
    <Base {...props}>
      <path d="M8.5 8 4.5 12l4 4" />
      <path d="M15.5 8l4 4-4 4" />
      <path d="M13.2 5.5 10.8 18.5" strokeWidth={1.5} />
    </Base>
  )
}

/** Video — play slab with timeline ticks */
export function MiniVideo(props: P) {
  return (
    <Base {...props}>
      <rect x="3" y="5.5" width="18" height="13" rx="3.5" />
      <path d="M10.2 9.4v5.2l4.6-2.6-4.6-2.6Z" fill="currentColor" stroke="none" />
      <path d="M7 21h10" strokeWidth={1.4} />
    </Base>
  )
}

/** Imagery — mountain frame + sun dot */
export function MiniImagery(props: P) {
  return (
    <Base {...props}>
      <rect x="3.5" y="4.5" width="17" height="15" rx="3.5" />
      <circle cx="9" cy="9.6" r="1.4" fill="currentColor" stroke="none" />
      <path d="M5 17.2 10 12.5l3.2 3.2 2.4-2.4 3.4 3.4" />
    </Base>
  )
}

/** Motion — clapperboard with motion streaks */
export function MiniMotion(props: P) {
  return (
    <Base {...props}>
      <path d="M4 9.5h16v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-9Z" />
      <path d="M4.5 5.8 19.6 4.4l.7 3.4-15.1 1.4-.7-3.4Z" />
      <path d="M8.5 5.4 10 8.7M12.5 5 14 8.3M16.4 4.7l1.5 3.3" strokeWidth={1.3} />
      <circle cx="12" cy="14.8" r="1.5" fill="currentColor" stroke="none" />
    </Base>
  )
}

/** Prompts — wand + sparkle burst */
export function MiniPrompts(props: P) {
  return (
    <Base {...props}>
      <path d="M5 19 13.5 10.5" />
      <path d="M14.5 4.5l1 2.6 2.6 1-2.6 1-1 2.6-1-2.6-2.6-1 2.6-1 1-2.6Z" />
      <circle cx="18.6" cy="15.4" r="1.2" fill="currentColor" stroke="none" />
    </Base>
  )
}

/** Education — graduation cap + tassel */
export function MiniEducation(props: P) {
  return (
    <Base {...props}>
      <path d="M12 4 2.8 8.6 12 13.2l9.2-4.6L12 4Z" />
      <path d="M6.5 11v4.2c0 1.4 2.5 2.6 5.5 2.6s5.5-1.2 5.5-2.6V11" />
      <path d="M19.5 9.5V15" strokeWidth={1.5} />
      <circle cx="19.5" cy="16.4" r="1.1" fill="currentColor" stroke="none" />
    </Base>
  )
}

/** Creativity — layered spark diamond */
export function MiniCreativity(props: P) {
  return (
    <Base {...props}>
      <path d="M12 3.5c.8 4.6 3.9 7.7 8.5 8.5-4.6.8-7.7 3.9-8.5 8.5-.8-4.6-3.9-7.7-8.5-8.5 4.6-.8 7.7-3.9 8.5-8.5Z" />
      <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
    </Base>
  )
}

/** Years — medal ribbon */
export function MiniMedal(props: P) {
  return (
    <Base {...props}>
      <circle cx="12" cy="9" r="5" />
      <path d="M12 6.8l.9 1.9 2 .3-1.5 1.4.4 2-1.8-1-1.8 1 .4-2L9.1 9l2-.3.9-1.9Z" />
      <path d="M9.5 13.2 8 20.5l4-2.2 4 2.2-1.5-7.3" />
    </Base>
  )
}

/** Students — two-person group mark */
export function MiniStudents(props: P) {
  return (
    <Base {...props}>
      <circle cx="9" cy="8.5" r="3" />
      <path d="M3.5 19c.6-3 2.8-4.8 5.5-4.8s4.9 1.8 5.5 4.8" />
      <circle cx="17" cy="9.5" r="2.3" />
      <path d="M16.5 14.4c2.2.3 3.6 1.8 4 4.1" />
    </Base>
  )
}

/** Projects — stacked layers cube */
export function MiniProjects(props: P) {
  return (
    <Base {...props}>
      <path d="M12 3.5 20 8l-8 4.5L4 8l8-4.5Z" />
      <path d="M4.5 12 12 16.2 19.5 12" />
      <path d="M4.5 15.8 12 20l7.5-4.2" />
    </Base>
  )
}
